/**
 * useDisplayMode Hook
 * Simplified for redirect-only mode (no embedded support)
 */

import { useMemo } from 'react';
import { sessionStateManager } from '../utils/SessionStateManager';

export type DisplayMode = 'standalone';

export interface DisplayModeConfig {
  mode: DisplayMode;
  isEmbedded: false;
  isStandalone: true;
  returnUrl: string | null;
  debugMode: boolean;
}

/**
 * Hook for display mode - always standalone in redirect flow
 * @returns DisplayModeConfig with standalone mode
 */
export function useDisplayMode(): DisplayModeConfig {
  return useMemo(() => {
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Always standalone mode (no embedded support)
    const mode: DisplayMode = 'standalone';

    // The return destination is populated only from server-resolved session state.
    let returnUrl: string | null = null;

    // Primary source: SessionStateManager (populated from authenticated config)
    const sessionState = sessionStateManager.getState();
    if (sessionState?.config?.returnUrl) {
      returnUrl = validateReturnUrl(sessionState.config.returnUrl);
    }

    // Check debug mode
    const debugMode = urlParams.get('debug') === 'true';

    return {
      mode,
      isEmbedded: false,
      isStandalone: true,
      returnUrl,
      debugMode,
    };
  }, []); // Only compute once on mount
}

/**
 * Utility to build URL with display mode parameters
 * Useful for SDK integration and testing
 */
export function buildDisplayModeUrl(
  baseUrl: string,
  config: {
    returnUrl?: string;
    apiKey?: string;
    debug?: boolean;
  }
): string {
  const url = new URL(baseUrl);

  // Add parameters (no embedded mode support)
  if (config.returnUrl) {
    url.searchParams.set('returnUrl', config.returnUrl);
  }

  if (config.apiKey) {
    url.searchParams.set('apiKey', config.apiKey);
  }

  if (config.debug !== undefined) {
    url.searchParams.set('debug', config.debug.toString());
  }

  return url.toString();
}

/**
 * Validates return URL to prevent open redirect vulnerabilities
 *
 * @param url - URL to validate
 * @returns Validated URL or null if invalid
 */
export function validateReturnUrl(url: string | null): string | null {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);

    // Only allow HTTPS in production (allow HTTP for localhost)
    const isLocalhost =
      parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';

    if (!isLocalhost && parsedUrl.protocol !== 'https:') {
      return null;
    }

    // Additional validation can be added here (allowlist, etc.)

    return url;
  } catch {
    return null;
  }
}
